"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.OpencuiClient = exports.ActionType = exports.MessageType = void 0;
const uuid_1 = require("uuid");
const sha1 = require("js-sha1");
var MessageType;
(function (MessageType) {
    MessageType[MessageType["Text"] = 0] = "Text";
    MessageType[MessageType["Card"] = 1] = "Card";
    MessageType[MessageType["Other"] = 2] = "Other";
})(MessageType || (exports.MessageType = MessageType = {}));
var ActionType;
(function (ActionType) {
    ActionType["click"] = "click";
    ActionType["reply"] = "reply";
})(ActionType || (exports.ActionType = ActionType = {}));
class OpencuiClient {
    constructor(config) {
        this.history = [];
        this.url = config.url;
        this.user = config.user;
        this.params = config.defaultParams || {};
        const urlCache = window.localStorage.getItem('copilot-url');
        if (urlCache && urlCache !== 'undefined') {
            this.url = urlCache;
        }
        const userCache = window.localStorage.getItem('copilot-user');
        if (userCache && userCache !== 'undefined') {
            this.user = JSON.parse(userCache);
        }
    }
    static create(config) {
        return new OpencuiClient(config);
    }
    registerContextGetter(contextGetter) {
        this.contextGetter = contextGetter;
    }
    registerActionHandler(handler) {
        this.actionHandler = handler;
    }
    executeAction(action) {
        if (!this.actionHandler) {
            throw new Error('no action handler registered');
        }
        this.actionHandler(action);
    }
    setUrl(url) {
        this.url = url;
        window.localStorage.setItem('copilot-url', url);
    }
    setUser(user, initialParams) {
        this.user = user;
        this.params = initialParams || new Map();
    }
    setDefaultParam(key, value) {
        this.params.set(key, value);
        // window.localStorage.setItem('copilot-initial-params', JSON.stringify(this.params));
    }
    getDefaultParam(key) {
        return this.params.get(key);
    }
    setDefaultParams(params) {
        this.params = params;
        // window.localStorage.setItem('copilot-initial-params', JSON.stringify(params));
    }
    getDefaultParams() {
        return this.params;
    }
    resetDefaultParams(initialParams) {
        this.params = initialParams;
    }
    // send an empty message to connect with copilot bot
    async connect(params) {
        return this.sendMessage({
            text: '',
            messageType: MessageType.Text,
            user: this.user || { id: 'unknown', name: 'unknown' },
        }, params);
    }
    async sendMessage(message, params, duHints, extra) {
        const initial = this.history.length === 0;
        if (message.text && message.text.length) {
            this.history.push(message);
        }
        const response = await fetch(this.url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                extra,
                restart: initial,
                text: message.text,
                user: this.user && this.user.name || '',
                sessionId: getSessionId(this.user && this.user.id || ''),
                messageId: getMessageId(this.user && this.user.id || ''),
                events: [{
                        type: 'PageContext',
                        packageName: 'ai.bethere.copilot',
                        slots: Object.entries(Object.assign(Object.assign(Object.assign({}, this.params), (this.contextGetter && this.contextGetter())), params)).map(([key, value]) => {
                            return {
                                value: typeof value === 'string' ? `"${value}"` : value,
                                attribute: key
                            };
                        }),
                        delta: false
                    }],
                hint: {
                    du: duHints || {
                        kind: "session",
                        entities: {
                            'io.opencui.core.FrameType': {}, 'io.opencui.core.EntityType': {}, 'io.opencui.core.SlotEntity': {}
                        }
                    }
                }
            }),
        }).catch((err) => {
            throw err;
        });
        let res;
        if (!response.ok) {
            throw new Error(`Failed to send message. Status code ${response.status}`);
        }
        else {
            res = await response.json().catch((err) => {
                throw err;
            });
        }
        const receivedMessages = (res.response || []).map((item) => {
            return this.parseResponse(item);
        }).filter(Boolean);
        this.history.push(...receivedMessages);
        if (this.history.length > 50) {
            this.history = this.history.slice(this.history.length - 50);
        }
        window.localStorage.setItem(this.getHistoryKey(), JSON.stringify(this.history));
        return receivedMessages;
    }
    getHistory() {
        if (!this.history.length) {
            const cache = window.localStorage.getItem(this.getHistoryKey());
            const history = cache && JSON.parse(cache);
            if (history && history.length) {
                this.history = history;
            }
        }
        return this.history;
    }
    clearHistory() {
        window.localStorage.removeItem(this.getHistoryKey());
        this.history = [];
    }
    getUser() {
        return this.user;
    }
    getHistoryKey() {
        return `copilot-history-${this.url}-${this.user && this.user.id}`;
    }
    parseResponse(res) {
        if (typeof res !== 'string') {
            return undefined;
        }
        let str = res;
        if (str.trim().startsWith('[')) {
            const end = res.indexOf(']');
            if (end !== -1) {
                str = str.substring(end + 1);
            }
        }
        if (!str || !str.length) {
            return undefined;
        }
        if (str.startsWith('{') && str.endsWith('}')) {
            try {
                const json = JSON.parse(str);
                return {
                    messageType: MessageType.Card,
                    title: json.title,
                    text: json.description,
                    user: {
                        id: this.url,
                        name: 'Copilot'
                    },
                    richMedia: json.richMedia && {
                        fileURL: json.richMedia.fileUrl,
                        altText: json.richMedia.altText
                    },
                    actionList: (json.insideAction || []).map((item) => ({
                        actionType: item.type,
                        url: item.url && item.url !== 'null' && item.url,
                        text: item.display,
                        actionParams: item.payload
                    }))
                };
            }
            catch (err) {
                return {
                    messageType: MessageType.Text,
                    text: str,
                    user: {
                        id: this.url,
                        name: 'Copilot'
                    }
                };
            }
        }
        return {
            messageType: MessageType.Text,
            text: str,
            user: {
                id: this.url,
                name: 'Copilot'
            }
        };
    }
}
exports.OpencuiClient = OpencuiClient;
function getSessionKey(userId) {
    return `copilot-session-${userId}`;
}
function getSessionId(userId) {
    let sessionId = window.sessionStorage.getItem(getSessionKey(userId));
    if (!sessionId) {
        sessionId = (0, uuid_1.v4)();
        window.sessionStorage.setItem(getSessionKey(userId), sessionId);
    }
    return sessionId;
}
function getMessageId(userId) {
    var timestamp = (new Date()).valueOf();
    return sha1(timestamp + userId);
}
exports.default = OpencuiClient;
